import React, { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'

import IPButton from '../../../../../components/IPButton'
import Table from '../../../../../components/Table'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPInput from '../../../../../components/IPInput'
import InputCampaign from '../../../../../components/InputCampaign'
import MoneySmallDecimals from '../../../../../components/IPPriceDisplay/IPPriceDisplayNew'

import Translations from '../../../../../translations'
import { translate } from '../../../../../base/localization'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../../base/customHooks'
import { getDateByTimezoneOffset } from '../../../../../base/utils/dataHelper'

import { IBrand, ICategoryList, ICompany } from '../../../components/models/models'
import { getBrandApi, getCategoryList, getCompanyApi } from '../../../components/api'
import { GetQuotaListApi } from '../../../ProductOperation/GeneralProductStats/services/getQuotaListApi'
import { QuotaListReturnModel } from '../../../ProductOperation/GeneralProductStats/models/models'

import {
  OpportunitMarketAnalysisRowDto,
  OpportunitMarketReportResponse,
  OpportunitReportRequestModel,
} from '../models/usageReport'
import { GetOpportunitMarketReportApi } from '../service/opportunityUsageApi'

interface IPageState {
  companies?: ICompany[]
  companyOption?: ISelectOption
  brandList?: IBrand[]
  categoryList?: ISelectOption[]
  quotaList?: ISelectOption[]

  dealerCode?: string
  productCode?: string
  brand?: ISelectOption
  categoryCode?: ISelectOption
  quota?: ISelectOption
  warehouse?: ISelectOption
  paymentMethod?: ISelectOption
  mudName?: ISelectOption

  nightMarket?: ISelectOption
  isIyzico?: ISelectOption
  hasNormalCampaign?: ISelectOption

  companyFilterOptions?: ISelectOption[]
  warehouseFilterOptions?: ISelectOption[]
  paymentMethodFilterOptions?: ISelectOption[]
  mudNameFilterOptions?: ISelectOption[]
}

const booleanOptions: ISelectOption[] = [
  { label: 'Hepsi', value: '' },
  { label: 'Evet', value: 'true' },
  { label: 'Hayır', value: 'false' },
]

function OpportunityMarketCampaignLikeReport(props: BaseComponentWithContextProps) {
  const t = translate(Translations)

  const getInitialState = (): IPageState => ({
    nightMarket: undefined,
    isIyzico: undefined,
    hasNormalCampaign: undefined,
  })

  const [state, setState] = useCustomReducer<IPageState>(getInitialState())
  const [reportData, setReportData] = useState<OpportunitMarketReportResponse>({
    Rows: [],
    Summary: undefined,
    ByCompany: [],
    ByWarehouse: [],
    ByBrand: [],
    ByProduct: [],
    ByCategory: [],
    ByQuota: [],
    ByDealer: [],
    ByPaymentMethod: [],
    ByMudName: [],
    ByNightMarket: [],
    ByIyzico: [],
    ByNormalCampaign: [],
    FilterOptions: {
      ProductCodes: [],
      QuotaNos: [],
      Brands: [],
      MudNames: [],
      WarehouseCodes: [],
      CompanyCodes: [],
      PaymentMethods: [],
      DealerCodes: [],
      CategoryCodes: [],
      NightMarketValues: [],
      IsIyzicoValues: [],
      HasNormalCampaignValues: [],
    },
  })

  const inititalStartDate = new Date()
  inititalStartDate.setDate(1)
  inititalStartDate.setMonth(7)
  inititalStartDate.setFullYear(2023)
  inititalStartDate.setHours(0)
  inititalStartDate.setMinutes(0)
  inititalStartDate.setSeconds(0)
  const [startDate, setStartDate] = useState(inititalStartDate)
  const [endDate, setEndDate] = useState(new Date())

  const getDate = async (data: any, title: string) => {
    if (title === 'Start') setStartDate(data)
    else setEndDate(data)
  }

  const toDateTimeLocal = date => {
    if (date != null) {
      return date
        .toLocaleString('sv-SE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
        .replace(' ', 'T')
    }
    return undefined
  }

  const parseBooleanOption = (option?: ISelectOption): boolean | undefined => {
    if (!option || option.value === '') return undefined
    return option.value === 'true'
  }

  const safeNumber = (value?: number | null) => Number(value || 0)

  const formatNumber = (value?: number | null) =>
    safeNumber(value).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

  const formatDate = (value: string | Date) => {
    if (!value) return ''
    const d = new Date(value)
    return d.toLocaleDateString('tr-TR')
  }

  const formatPriceType = (value: any) =>
    value == 'TL' || value == 'TRY' ? '₺' : value == 'USD' ? '$' : value == 'EUR' ? '€' : value

  const getCompanies = async () => {
    const company: any[] = await getCompanyApi()
    const data: ICompany[] = []
    company.filter(com => com.Name !== 'Index Group').forEach(co => data.push(co))
    setState({ companies: data })
  }

  const getCategory = async () => {
    const data: ICategoryList[] = await getCategoryList()
    const categoryList: ISelectOption[] = []
    if (data) {
      data.forEach(dt => categoryList.push({ value: dt.SapCode, label: `${dt.Name} (${dt.SapCode})` }))
    }
    setState({ categoryList })
  }

  const getQuota = async () => {
    const api = new GetQuotaListApi({})
    const data = await api.getQuotaList()
    const quotaList: ISelectOption[] = []
    if (data) {
      data.forEach((dt: QuotaListReturnModel) =>
        quotaList.push({ value: dt.ZZKTN, label: `${dt.ZZKTN}` }),
      )
    }
    setState({ quotaList: quotaList.length > 0 ? quotaList : undefined })
  }

  const getBrand = async () => {
    const data: any[] = await getBrandApi()
    setState({ brandList: data })
  }

  useEffect(() => {
    getCompanies()
    getBrand()
    getQuota()
    getCategory()
  }, [])

  const bindFilterOptions = (res: OpportunitMarketReportResponse) => {
    setState({
      companyFilterOptions: (res.FilterOptions?.CompanyCodes || []).map(x => ({
        value: x,
        label: state.companies?.find(company => company.Code.toString() === x)?.Name || x,
      })),
      warehouseFilterOptions: (res.FilterOptions?.WarehouseCodes || []).map(x => ({ value: x, label: x })),
      paymentMethodFilterOptions: (res.FilterOptions?.PaymentMethods || []).map(x => ({ value: x, label: x })),
      mudNameFilterOptions: (res.FilterOptions?.MudNames || []).map(x => ({ value: x, label: x })),
    })
  }

  const getReport = async () => {
    props.showLoading()

    const api = new GetOpportunitMarketReportApi({})

    const req: OpportunitReportRequestModel = {
      StartDate: startDate ? getDateByTimezoneOffset(startDate) : undefined,
      EndDate: endDate ? getDateByTimezoneOffset(endDate) : undefined,
      DealerCodes: state.dealerCode ? [state.dealerCode] : undefined,
      ProductCodes: state.productCode ? state.productCode.split('@@').map(x => x.trim()).filter(Boolean) : undefined,
      CompanyCodes: state.companyOption ? [state.companyOption.value.toString()] : undefined,
      Brands: state.brand ? [state.brand.label.toString()] : undefined,
      CategoryCodes: state.categoryCode ? [state.categoryCode.value.toString()] : undefined,
      QuotaNos: state.quota ? [state.quota.value.toString()] : undefined,
      WarehouseCodes: state.warehouse ? [state.warehouse.value.toString()] : undefined,
      PaymentMethods: state.paymentMethod ? [state.paymentMethod.value.toString()] : undefined,
      MudNames: state.mudName ? [state.mudName.value.toString()] : undefined,
      NightMarket: parseBooleanOption(state.nightMarket),
      IsIyzico: parseBooleanOption(state.isIyzico),
      HasNormalCampaign: parseBooleanOption(state.hasNormalCampaign),
    }

    try {
      const res = await api.getReport(req)
      setReportData(res)
      bindFilterOptions(res)
      props.hideLoading()
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Fırsat Pazarı kullanım raporu alınırken hata oluştu.',
      })
    }
  }

  const rawTableColumns = useMemo(
    () => [
      { Header: 'Sipariş No', accessor: 'orderNumber' },
      { Header: 'Tarih', accessor: 'orderDate' },
      { Header: 'Şirket', accessor: 'companyName' },
      { Header: 'Depo', accessor: 'warehouseCode' },
      { Header: 'Bayi Kodu', accessor: 'dealerCode' },
      { Header: 'Bayi Ünvan', accessor: 'dealerName' },
      { Header: 'Ürün Kodu', accessor: 'productCode' },
      { Header: 'Ürün Adı', accessor: 'productName' },
      { Header: 'Marka', accessor: 'brand' },
      { Header: 'Kategori', accessor: 'category' },
      { Header: 'Kota', accessor: 'quota' },
      { Header: 'Ürün Müdürü', accessor: 'mudName' },
      { Header: 'Ödeme Yöntemi', accessor: 'paymentMethod' },
      { Header: 'Gece Pazarı', accessor: 'nightMarket' },
      { Header: 'İyzico', accessor: 'isIyzico' },
      { Header: 'Normal Kampanya', accessor: 'hasNormalCampaign' },
      { Header: 'Adet', accessor: 'quantity' },
      { Header: 'Birim Fiyat', accessor: 'unitPrice' },
      { Header: 'Toplam Tutar', accessor: 'revenue' },
      { Header: 'Para Birimi', accessor: 'currency' },
    ],
    [],
  )

  const analysisTableColumns = useMemo(
    () => [
      { Header: 'Kod', accessor: 'groupKey' },
      { Header: 'Ad', accessor: 'groupName' },
      { Header: 'Kayıt Sayısı', accessor: 'recordCount' },
      { Header: 'Sipariş Sayısı', accessor: 'distinctOrderCount' },
      { Header: 'Toplam Adet', accessor: 'totalQuantity' },
      { Header: 'Tekil Bayi', accessor: 'distinctDealerCount' },
      { Header: 'Tekil Ürün', accessor: 'distinctProductCount' },
      { Header: 'Tekil Fırsat', accessor: 'distinctOpportunityCount' },
      { Header: 'Ciro', accessor: 'revenueByCurrency' },
    ],
    [],
  )

  const summary = reportData?.Summary

  const rawTableData = useMemo(() => {
    return (reportData.Rows || []).map(item => ({
      orderNumber: item.OrderNumber,
      orderDate: formatDate(item.OrderDate),
      companyName: item.CompanyName || item.CompanyCode,
      warehouseCode: item.WarehouseCode,
      dealerCode: item.DealerCode?.replace('0000', ''),
      dealerName: item.DealerName,
      productCode: item.ProductCode,
      productName: item.Name,
      brand: item.Brand,
      category: state.categoryList?.find(c => c.value === item.CategoryCode)?.label || item.CategoryCode,
      quota: item.QuotaNo,
      mudName: item.MudName,
      paymentMethod: item.PaymentMethod,
      nightMarket: item.NightMarket,
      isIyzico: item.IsIyzico,
      hasNormalCampaign: item.HasNormalCampaign,
      quantity: item.Quantity,
      unitPrice: <MoneySmallDecimals value={safeNumber(item.UnitPrice)} currency={formatPriceType(item.OrderCurrency)} />,
      revenue: <MoneySmallDecimals value={safeNumber(item.Revenue)} currency={formatPriceType(item.OrderCurrency)} />,
      currency: item.OrderCurrency,
    }))
  }, [reportData.Rows, state.categoryList, state.quotaList])

  const mapAnalysisTableData = (items: OpportunitMarketAnalysisRowDto[], type?: string) => {
    return items.map(item => ({
      groupKey: item.GroupKey,
      groupName:
        type === 'Category'
          ? state.categoryList?.find(c => c.value === item.GroupName)?.label || item.GroupName
          : type === 'Quota'
            ? state.quotaList?.find(q => q.value === item.GroupName)?.label || item.GroupName
            : item.GroupName,
      recordCount: item.RecordCount,
      distinctOrderCount: item.DistinctOrderCount,
      totalQuantity: item.TotalQuantity,
      distinctDealerCount: item.DistinctDealerCount,
      distinctProductCount: item.DistinctProductCount,
      distinctOpportunityCount: item.DistinctOpportunityCount,
      revenueByCurrency: (
        <div>
          {(item.RevenueByCurrency || []).map((r, idx) => (
            <div key={`${r.Currency}-${idx}`}>
              <MoneySmallDecimals value={safeNumber(r.Revenue)} currency={formatPriceType(r.Currency)} />
            </div>
          ))}
        </div>
      ),
    }))
  }

  const byCompanyTableData = useMemo(() => mapAnalysisTableData(reportData.ByCompany || []), [reportData.ByCompany, state.categoryList, state.quotaList])
  const byWarehouseTableData = useMemo(() => mapAnalysisTableData(reportData.ByWarehouse || []), [reportData.ByWarehouse, state.categoryList, state.quotaList])
  const byBrandTableData = useMemo(() => mapAnalysisTableData(reportData.ByBrand || []), [reportData.ByBrand, state.categoryList, state.quotaList])
  const byProductTableData = useMemo(() => mapAnalysisTableData(reportData.ByProduct || []), [reportData.ByProduct, state.categoryList, state.quotaList])
  const byCategoryTableData = useMemo(() => mapAnalysisTableData(reportData.ByCategory || [], 'Category'), [reportData.ByCategory, state.categoryList, state.quotaList])
  const byQuotaTableData = useMemo(() => mapAnalysisTableData(reportData.ByQuota || [], 'Quota'), [reportData.ByQuota, state.categoryList, state.quotaList])
  const byDealerTableData = useMemo(() => mapAnalysisTableData(reportData.ByDealer || []), [reportData.ByDealer, state.categoryList, state.quotaList])
  const byPaymentMethodTableData = useMemo(() => mapAnalysisTableData(reportData.ByPaymentMethod || []), [reportData.ByPaymentMethod, state.categoryList, state.quotaList])
  const byMudNameTableData = useMemo(() => mapAnalysisTableData(reportData.ByMudName || []), [reportData.ByMudName, state.categoryList, state.quotaList])
  const byNightMarketTableData = useMemo(() => mapAnalysisTableData(reportData.ByNightMarket || []), [reportData.ByNightMarket, state.categoryList, state.quotaList])
  const byIyzicoTableData = useMemo(() => mapAnalysisTableData(reportData.ByIyzico || []), [reportData.ByIyzico, state.categoryList, state.quotaList])
  const byNormalCampaignTableData = useMemo(() => mapAnalysisTableData(reportData.ByNormalCampaign || []), [reportData.ByNormalCampaign, state.categoryList, state.quotaList])

  const excelButton = async () => {
    try {
      const wb = XLSX.utils.book_new()

      const appendSheet = (sheetName: string, data: any[]) => {
        const ws = XLSX.utils.json_to_sheet(data.length > 0 ? data : [{}])

        // Sayısal hücrelere format uygula
        if (data.length > 0) {
          const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
          for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
              if (ws[cellAddress] && typeof ws[cellAddress].v === 'number') {
                ws[cellAddress].z = '0.00'
              }
            }
          }
        }

        XLSX.utils.book_append_sheet(wb, ws, sheetName)
      }

      appendSheet('Ham Data', rawTableData.map(x => ({
        SiparisNo: x.orderNumber,
        Tarih: x.orderDate,
        Sirket: x.companyName,
        Depo: x.warehouseCode,
        BayiKodu: x.dealerCode,
        BayiUnvan: x.dealerName,
        UrunKodu: x.productCode,
        UrunAdi: x.productName,
        Marka: x.brand,
        Kategori: x.category,
        Kota: x.quota,
        UrunMuduru: x.mudName,
        OdemeYontemi: x.paymentMethod,
        GecePazari: x.nightMarket,
        Iyzico: x.isIyzico,
        NormalKampanya: x.hasNormalCampaign,
        Adet: x.quantity,
        BirimFiyat: (reportData.Rows?.find(r => r.OrderNumber === x.orderNumber)?.UnitPrice) || 0,
        ToplamTutar: (reportData.Rows?.find(r => r.OrderNumber === x.orderNumber)?.Revenue) || 0,
        ParaBirimi: x.currency,
      })))

      appendSheet('Ozet', summary ? [{
        KayitSayisi: summary.RecordCount,
        SiparisSayisi: summary.DistinctOrderCount,
        ToplamAdet: summary.TotalQuantity,
        FarkliBayi: summary.DistinctDealerCount,
        FarkliUrun: summary.DistinctProductCount,
        FarkliFirsat: summary.DistinctOpportunityCount,
        FarkliSirket: summary.DistinctCompanyCount,
        FarkliDepo: summary.DistinctWarehouseCount,
      }] : [])

      // Tüm olası para birimlerini bul
      const allAnalysisData = [
        reportData.ByCompany || [],
        reportData.ByWarehouse || [],
        reportData.ByBrand || [],
        reportData.ByProduct || [],
        reportData.ByCategory || [],
        reportData.ByQuota || [],
        reportData.ByDealer || [],
        reportData.ByPaymentMethod || [],
        reportData.ByMudName || [],
        reportData.ByNightMarket || [],
        reportData.ByIyzico || [],
        reportData.ByNormalCampaign || [],
      ]

      const currencies = Array.from(
        new Set(
          allAnalysisData.flatMap(items =>
            items.flatMap(item => (item.RevenueByCurrency || []).map(r => r.Currency))
          )
        )
      ).sort()

      const formatAnalysisDataWithCurrencies = (items: OpportunitMarketAnalysisRowDto[]) => {
        return items.map(item => {
          const row: any = {
            Kod: item.GroupKey,
            Ad: item.GroupName,
            KayitSayisi: item.RecordCount,
            SiparisSayisi: item.DistinctOrderCount,
            ToplamAdet: item.TotalQuantity,
            TekilBayi: item.DistinctDealerCount,
            TekilUrun: item.DistinctProductCount,
            TekilFirsat: item.DistinctOpportunityCount,
          }

          currencies.forEach(currency => {
            const revenue = item.RevenueByCurrency?.find(r => r.Currency === currency)?.Revenue
            row[`Ciro (${currency})`] = revenue || 0
          })

          return row
        })
      }

      appendSheet('Sirket Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByCompany || []))
      appendSheet('Depo Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByWarehouse || []))
      appendSheet('Marka Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByBrand || []))
      appendSheet('Urun Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByProduct || []))
      appendSheet('Kategori Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByCategory || []))
      appendSheet('Kota Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByQuota || []))
      appendSheet('Bayi Bazli Analiz', formatAnalysisDataWithCurrencies(reportData.ByDealer || []))
      appendSheet('Odeme Yontemi Analiz', formatAnalysisDataWithCurrencies(reportData.ByPaymentMethod || []))
      appendSheet('Urun Muduru Analiz', formatAnalysisDataWithCurrencies(reportData.ByMudName || []))
      appendSheet('Gece Pazari Analiz', formatAnalysisDataWithCurrencies(reportData.ByNightMarket || []))
      appendSheet('Iyzico Analiz', formatAnalysisDataWithCurrencies(reportData.ByIyzico || []))
      appendSheet('Normal Kampanya Analiz', formatAnalysisDataWithCurrencies(reportData.ByNormalCampaign || []))

      XLSX.writeFile(wb, `FirsatPazariKullanimRaporu_${toDateTimeLocal(new Date())}.xlsx`)
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || err?.message || 'Excel oluşturulurken hata oluştu.',
        allowOutsideClick: true,
        backdrop: true,
      })
    }
  }

  return (
    <div className='content col-lg-12 m-auto'>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <span className='h4 mb-0'>Fırsat Pazarı Kullanım Raporu</span>
          <div className='d-flex align-items-center'>
            <span>Excel: </span>
            <button
              type='button'
              style={{
                border: '1px solid #2e7d32',
                borderRadius: 0,
                width: '29px',
                height: '29px',
                backgroundColor: 'white',
                margin: '0 10px',
              }}
              onClick={excelButton}
              className='btn text-capitalize background-white-important d-flex align-items-center'
            >
              <i className='fas fa-file-excel' style={{ marginLeft: '-6px', fontSize: '18px', color: '#2e7d32' }}></i>
            </button>
          </div>
        </div>

        <div className='card-body'>
          <form className='formbanner'>
            <div className='row mb-3'>
              <div className='col-12 col-md-1 mb-3'></div>

              <div className='col-12 col-md-2 mb-3'>
                <InputCampaign
                  type='date'
                  id='StartDate'
                  name='StartDate'
                  label='Başlangıç Tarihi'
                  value={toDateTimeLocal(startDate)}
                  onChange={e => getDate(e.target.value, 'Start')}
                  isActive
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <InputCampaign
                  type='date'
                  id='EndDate'
                  name='EndDate'
                  label='Bitiş Tarihi'
                  value={toDateTimeLocal(endDate)}
                  onChange={e => getDate(e.target.value, 'End')}
                  isActive
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  options={
                    state.companies?.map((item: ICompany) => ({
                      label: item.Name,
                      value: `${item.Code}`,
                    })) || []
                  }
                  placeholder='Şirket Seçiniz...'
                  value={state.companyOption}
                  onChangeSingle={option => setState({ companyOption: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPInput
                  type='text'
                  id='DealerCode'
                  name='DealerCode'
                  placeholder='Bayi Kodu'
                  value={state.dealerCode}
                  onChange={e => setState({ dealerCode: e.target.value })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPInput
                  type='text'
                  id='ProductCode'
                  name='ProductCode'
                  placeholder='Ürün Kodu (@@ ile çoklu)'
                  value={state.productCode}
                  onChange={e => setState({ productCode: e.target.value })}
                />
              </div>

              <div className='col-12 col-md-1 mb-3'></div>

              <div className='col-12 col-md-1 mb-3'></div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='Brand'
                  name='Brand'
                  placeholder='Marka seçiniz...'
                  options={state.brandList?.map(brand => ({ label: brand.Name, value: brand.Id })) || []}
                  value={state.brand as unknown as ISelectOption}
                  onChangeSingle={option => setState({ brand: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  placeholder='Kategori seçiniz...'
                  value={state.categoryCode}
                  options={state.categoryList}
                  isClearable
                  onChangeSingle={option => setState({ categoryCode: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='Quota'
                  name='Quota'
                  placeholder='Kota seçiniz...'
                  options={state.quotaList}
                  value={state.quota}
                  onChangeSingle={option => setState({ quota: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='Warehouse'
                  name='Warehouse'
                  placeholder='Depo seçiniz...'
                  options={state.warehouseFilterOptions}
                  value={state.warehouse}
                  onChangeSingle={option => setState({ warehouse: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='PaymentMethod'
                  name='PaymentMethod'
                  placeholder='Ödeme Yöntemi seçiniz...'
                  options={state.paymentMethodFilterOptions}
                  value={state.paymentMethod}
                  onChangeSingle={option => setState({ paymentMethod: option })}
                />
              </div>

              <div className='col-12 col-md-1 mb-3'></div>
              <div className='col-12 col-md-1 mb-3'></div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='MudName'
                  name='MudName'
                  placeholder='Ürün Müdürü seçiniz...'
                  options={state.mudNameFilterOptions}
                  value={state.mudName}
                  onChangeSingle={option => setState({ mudName: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='NightMarket'
                  name='NightMarket'
                  placeholder='Gece Pazarı'
                  options={booleanOptions}
                  value={state.nightMarket}
                  onChangeSingle={option => setState({ nightMarket: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='IsIyzico'
                  name='IsIyzico'
                  placeholder='İyzico'
                  options={booleanOptions}
                  value={state.isIyzico}
                  onChangeSingle={option => setState({ isIyzico: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPSelectBox
                  isClearable
                  id='HasNormalCampaign'
                  name='HasNormalCampaign'
                  placeholder='Normal Kampanya'
                  options={booleanOptions}
                  value={state.hasNormalCampaign}
                  onChangeSingle={option => setState({ hasNormalCampaign: option })}
                />
              </div>

              <div className='col-12 col-md-2 mb-3'>
                <IPButton text='Göster' onClick={getReport} />
              </div>
            </div>
          </form>

          {summary && (
            <div className='row mb-4'>
              <div className='col-12 col-md-3 mb-3'></div>
              <div className='col-md-3 mb-3'>
                <div className='card'><div className='card-body'>
                  <div><b>Kayıt Sayısı:</b> {summary.RecordCount}</div>
                  <div><b>Sipariş Sayısı:</b> {summary.DistinctOrderCount}</div>
                  <div><b>Toplam Adet:</b> {summary.TotalQuantity}</div>
                </div></div>
              </div>
              <div className='col-md-3 mb-3'>
                <div className='card'><div className='card-body'>
                  <div><b>Farklı Bayi:</b> {summary.DistinctDealerCount}</div>
                  <div><b>Farklı Ürün:</b> {summary.DistinctProductCount}</div>
                  <div><b>Farklı Fırsat:</b> {summary.DistinctOpportunityCount}</div>
                </div></div>
              </div>
            </div>
          )}

          <div className='m-4'>
            <h5>Ham Data</h5>
            <Table columns={rawTableColumns} data={rawTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Şirket Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byCompanyTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Depo Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byWarehouseTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Marka Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byBrandTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Ürün Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byProductTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Kategori Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byCategoryTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Kota Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byQuotaTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Bayi Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byDealerTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Ödeme Yöntemi Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byPaymentMethodTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Ürün Müdürü Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byMudNameTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Gece Pazarı Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byNightMarketTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>İyzico Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byIyzicoTableData} currentPage={0} />
          </div>

          <div className='m-4'>
            <h5>Normal Kampanya Bazlı Analiz</h5>
            <Table columns={analysisTableColumns} data={byNormalCampaignTableData} currentPage={0} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(OpportunityMarketCampaignLikeReport)